import { rawLocalStorage } from "@sfc/ui/local-storage";
import { trySetItem } from "@client/utils/storageWrite";
import { useSyncExternalStore } from "react";
import { z } from "zod";

/**
 * The round the iPhone shell is showing, persisted on the device. Lives in its
 * own module so the shell's leaves (the presence recorder's page source) can
 * name the round without importing the app component that mounts them.
 *
 * Round names are remembered for every round ever selected, so the round
 * selector can still label rounds whose videos are on the device while
 * offline.
 */
export type IPhoneRound = { id: string; name: string };

const storageKey = "iphoneRound.v1";

const roundStateSchema = z.object({
  selectedRoundId: z.string().nullable().catch(null),
  roundNames: z.record(z.string(), z.string()).catch({}),
});

type RoundState = z.infer<typeof roundStateSchema>;

const emptyState: RoundState = { selectedRoundId: null, roundNames: {} };
const listeners = new Set<() => void>();

// Loaded on first read, not at import: importing this module must not touch
// localStorage (DOM-less tests import the shell's leaves).
let state: RoundState | null = null;

export function useSelectedRound(): IPhoneRound | null {
  const { selectedRoundId, roundNames } = useSyncExternalStore(
    subscribeSelectedRound,
    getRoundState,
    getRoundState,
  );

  if (selectedRoundId == null) {
    return null;
  }

  return { id: selectedRoundId, name: roundNames[selectedRoundId] ?? "" };
}

export function useRoundNames(): Record<string, string> {
  return useSyncExternalStore(
    subscribeSelectedRound,
    getRoundState,
    getRoundState,
  ).roundNames;
}

export function getSelectedRoundId(): string | null {
  return getRoundState().selectedRoundId;
}

export function selectRound(round: IPhoneRound): void {
  const current = getRoundState();

  setRoundState({
    selectedRoundId: round.id,
    roundNames: { ...current.roundNames, [round.id]: round.name },
  });
}

/** Back to the round picker, keeping the remembered round names. */
export function deselectRound(): void {
  setRoundState({ ...getRoundState(), selectedRoundId: null });
}

/**
 * User-scoped state on a device-scoped key, so every identity transition
 * drops it (see `iphone/lib/auth.ts`), or the next account would open in the
 * previous user's round.
 */
export function clearRoundState(): void {
  setRoundState(emptyState);
}

export function subscribeSelectedRound(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getRoundState(): RoundState {
  state ??= loadRoundState();

  return state;
}

function setRoundState(next: RoundState): void {
  state = next;
  trySetItem(rawLocalStorage, storageKey, JSON.stringify(next));

  for (const listener of listeners) {
    listener();
  }
}

function loadRoundState(): RoundState {
  if (typeof window === "undefined") {
    return emptyState;
  }

  try {
    const raw = rawLocalStorage.getItem(storageKey);

    if (raw == null) {
      return emptyState;
    }

    return roundStateSchema.parse(JSON.parse(raw));
  } catch {
    return emptyState;
  }
}
